import { uiText } from '../../../utils/uiTranslation';
import React from 'react';
import { ManpowerRequestSettings } from '../../ManpowerRequest';

const ToggleRow = ({ busy, checked, description, disabled, label, onChange, t }) => (
  <div className={`profile-control-row ${disabled ? 'disabled' : ''}`}>
    <div className="profile-control-copy">
      <strong>{uiText(label)}</strong>
      <p>{description}</p>
    </div>
    <select
      className="profile-approval-select"
      aria-label={uiText(label)}
      value={checked ? 'enabled' : 'disabled'}
      onChange={(e) => onChange(e.target.value === 'enabled')}
      disabled={disabled || busy}
    >
      <option value="disabled">{t('controls.disabled', 'Disabled')}</option>
      <option value="enabled">{t('controls.enabled', 'Enabled')}</option>
    </select>
  </div>
);

const ControlsSection = ({
  approvalBusy,
  approvalJobPostings,
  approvalOfferLetters,
  approvalSettingsLoading,
  canManageCompanySettings,
  companyData,
  handleToggleAgencyJobPosting,
  handleApplicationMethodChange,
  applicationRoutingBusy,
  handleToggleCommunityFeature,
  handleToggleJobApprovals,
  handleToggleOfferApprovals,
  isRTL,
  t,
  userUID
}) => {
  return (
    <div className="profile-section-stack">
      <div className="profile-controls-grid">
        <section className="profile-panel">
          <div className="profile-panel-header">
            <div>
              <h2>{t('controls.approvalTitle', 'Approval workflows')}</h2>
              <p>{t('controls.approvalDesc', 'Set review checkpoints for the most sensitive actions in the workspace.')}</p>
            </div>
          </div>

          {!approvalSettingsLoading && (
            <div className="profile-control-stack">
              <ToggleRow
                busy={false}
                checked={companyData?.customFeatures?.community !== false}
                description={t('controls.communityDesc', 'Enable or disable the community workspace across the company.')}
                disabled={!canManageCompanySettings}
                label={t('controls.communityLabel', 'Community workspace')}
                onChange={(value) => handleToggleCommunityFeature(value)}
                t={t}
              />
              <ToggleRow
                busy={approvalBusy.jobPostings}
                checked={approvalJobPostings}
                description={t('approvals.jobPostingsHint', 'Job posts must be approved before they go live.')}
                disabled={!canManageCompanySettings}
                label={t('approvals.jobPostings', 'Require approvals for job postings')}
                onChange={(value) => handleToggleJobApprovals(value)}
                t={t}
              />
              <ToggleRow
                busy={approvalBusy.offerLetters}
                checked={approvalOfferLetters}
                description={t('approvals.offerLettersHint', 'Offers must be approved before they are sent.')}
                disabled={!canManageCompanySettings}
                label={t('approvals.offerLetters', 'Require approvals for offer letters')}
                onChange={(value) => handleToggleOfferApprovals(value)}
                t={t}
              />
            </div>
          )}

          {!canManageCompanySettings && (
            <p className="profile-inline-helper">
              {t('controls.viewOnly', 'You can view approval status here, but only workspace settings managers can change it.')}
            </p>
          )}
        </section>

        <section className="profile-panel">
          <div className="profile-panel-header">
            <div>
              <h2>{t('controls.jobPostingTitle', 'Job posting')}</h2>
              <p>{t('controls.jobPostingDesc', 'Choose how recruiters can present jobs to candidates.')}</p>
            </div>
          </div>
          <div className="profile-control-stack">
            <div className="profile-control-row">
              <div className="profile-control-copy">
                <strong>{t('controls.applicationRouting', 'Candidate application destination')}</strong>
                <p>{t('controls.applicationRoutingDesc', 'Applies to new jobs. Existing jobs keep their destination. Collecting applications in JOBSEEQR requires ATS access; otherwise jobs use an external destination.')}</p>
              </div>
              <select
                className="profile-approval-select"
                aria-label={t('controls.applicationRouting', 'Candidate application destination')}
                value={companyData?.defaultApplicationMethod || 'ats'}
                disabled={!canManageCompanySettings || applicationRoutingBusy}
                onChange={event => handleApplicationMethodChange(event.target.value)}
              >
                <option value="ats">{t('controls.applicationJobseeqr', 'JOBSEEQR')}</option>
                <option value="external">{t('controls.applicationExternal', 'External link, email or WhatsApp')}</option>
              </select>
            </div>
            <ToggleRow
              busy={false}
              checked={companyData?.agencyJobPostingEnabled === true}
              description={t('controls.agencyPostingDesc', 'Allow recruiters to enter a client company name in the job posting popup.')}
              disabled={!canManageCompanySettings}
              label={t('controls.agencyPostingLabel', 'Post jobs for client companies')}
              onChange={handleToggleAgencyJobPosting}
              t={t}
            />
          </div>
        </section>

        <section className="profile-panel">
          <div className="profile-panel-header">
            <div>
              <h2>{t('controls.manpowerTitle', 'Manpower request form')}</h2>
              <p>{t('controls.manpowerDesc', 'Allow department managers to submit structured hiring requests through a controlled intake flow.')}</p>
            </div>
          </div>

          {canManageCompanySettings ? (
            <div className="profile-manpower-shell">
              <ManpowerRequestSettings companyUID={userUID} companyName={companyData.companyName} isRTL={isRTL} />
            </div>
          ) : (
            <div className="profile-controls-lockup">
              <i className="fa-solid fa-lock"></i>
              <strong>{t('controls.lockTitle', 'Settings access required')}</strong>
              <p>{t('controls.lockDesc', 'Manpower request configuration is available only to workspace settings managers.')}</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ControlsSection;
